'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Separator } from '@music-vine/cadence/ui';
import { logEvent } from '@/pages/api/v1/utils';
import { cn } from '@/lib/utils';
const groups = [{
  name: 'Product',
  links: [{
    label: 'Royalty-free music',
    href: '/music',
    type: 'footer_music',
    openInNewTab: false
  }, {
    label: 'Sound effects',
    href: '/sfx',
    type: 'footer_sfx',
    openInNewTab: false
  }, {
    label: 'Stock video',
    href: '/stock-video',
    type: 'footer_stock_video',
    openInNewTab: false
  }, {
    label: 'Motion graphics',
    href: '/motion-graphics',
    type: 'footer_motion_graphics',
    openInNewTab: false
  }, {
    label: 'LUTs',
    href: '/luts',
    type: 'footer_lut',
    openInNewTab: false
  }, {
    label: 'API',
    href: '/api',
    type: 'footer_api',
    openInNewTab: false
  }]
}, {
  name: 'Discover',
  links: [{
    label: 'Artists',
    href: '/contributors',
    type: 'footer_all_artists',
    openInNewTab: false
  }, {
    label: 'Music collections',
    href: '/music/collection',
    type: 'footer_music_collections',
    openInNewTab: false
  }, {
    label: 'Sound effect collections',
    href: '/sfx/collection',
    type: 'footer_sfx_collections',
    openInNewTab: false
  }, {
    label: 'Video categories',
    href: '/stock-video/explore',
    type: 'footer_stock_video_explore',
    openInNewTab: false
  }, {
    label: 'Playlist generator',
    href: '/playlist-generator',
    type: 'footer_ai_playlist_generator',
    openInNewTab: false
  }]
}, {
  name: 'Resources',
  links: [{
    label: 'Help center',
    href: '/help-center/general-faqs',
    type: 'footer_help_center',
    openInNewTab: false
  }, {
    label: 'Blog',
    href: '/blog',
    type: 'footer_blog',
    openInNewTab: false
  }, {
    label: 'Referral program',
    href: '/refer-and-earn',
    type: 'footer_referral_program',
    openInNewTab: false
  }, {
    label: 'Discord server',
    href: 'https://discord.com/invite/q5xaCqhGFC',
    type: 'footer_social_discord',
    openInNewTab: true
  }]
}, {
  name: 'Company',
  links: [{
    label: 'About us',
    href: '/about',
    type: 'footer_about',
    openInNewTab: false
  }, {
    label: 'Pricing',
    href: '/pricing',
    type: 'footer_pricing',
    openInNewTab: false
  }, {
    label: 'Press',
    href: '/press',
    type: 'footer_press',
    openInNewTab: false
  }, {
    label: 'Careers',
    href: 'https://musicvine.com/careers',
    type: 'footer_careers',
    openInNewTab: true
  }]
}];
export const PageLinks = () => {
  const asPath = usePathname();
  const [accordionValue, setAccordionValue] = useState<string>('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: asPath is used to reset the accordion value
  useEffect(() => {
    setAccordionValue('');
  }, [asPath]);
  const logFooterClick = useCallback((type: string, text: string, nextPath: string) => {
    void logEvent({
      event_type: 'click',
      asPath: asPath ?? '',
      metadata: {
        type,
        text,
        next_path: nextPath
      }
    });
  }, [asPath]);
  return <>
      {/* Mobile accordion version */}
      <div className="md:hidden">
        <Accordion collapsible onValueChange={setAccordionValue} type="single" value={accordionValue} data-sentry-element="Accordion" data-sentry-source-file="PageLinks.tsx">
          {groups.map(item => <AccordionItem className="border-b-0 px-0!" key={item.name} value={item.name}>
              <AccordionTrigger className='my-0 mt-4 pb-1 text-left font-aeonik font-semibold text-gray-950 text-lg transition-all dark:text-white [&>[data-icon="closed"]]:data-[state=closed]:block [&>[data-icon="open"]]:data-[state=open]:block [&>[data-icon]]:hidden' triggerChildrenClassName="px-3 hover:bg-transparent dark:hover:bg-transparent">
                {item.name}
              </AccordionTrigger>
              <AccordionContent className="px-3 pt-0 pb-4">
                <ul className="flex list-none flex-col gap-4" key={item.name}>
                  {item.links.map((link, index) => <li className={cn('font-normal font-sans text-sm 2xl:text-base', index === 0 && 'pt-2')} key={link.label}>
                      <Link className="text-gray-700 hover:text-gray-500 dark:text-gray-400" href={link.href} onClick={() => logFooterClick(link.type, link.label, link.href)} rel={link.openInNewTab ? 'noopener' : undefined} target={link.openInNewTab ? '_blank' : '_self'}>
                        {link.label}
                      </Link>
                    </li>)}
                </ul>
              </AccordionContent>
              <Separator />
            </AccordionItem>)}
        </Accordion>
      </div>

      {/* Desktop list version */}
      <div className="hidden md:contents">
        {groups.map(group => <ul className="flex list-none flex-col gap-2" key={group.name}>
            <li>
              <div className="font-heading font-semibold text-gray-950 text-lg 2xl:text-xl dark:text-white">
                {group.name}
              </div>
            </li>
            {group.links.map(link => <li className="font-normal font-sans text-sm 2xl:text-base" key={link.label}>
                <Link className="text-gray-700 hover:text-gray-500 dark:text-gray-200" href={link.href} onClick={() => logFooterClick(link.type, link.label, link.href)} rel={link.openInNewTab ? 'noopener' : undefined} target={link.openInNewTab ? '_blank' : '_self'}>
                  {link.label}
                </Link>
              </li>)}
          </ul>)}
      </div>
    </>;
};